// API Configuration: in development default to local backend
// Use relative URLs — same-origin requests bypass Cloudflare managed challenge
const API_BASE_URL = '';

export const API_ENDPOINTS = {
  VALIDATE_REFERRAL_CODE: `${API_BASE_URL}/api/auth/validate-referral-code`,
  REGISTER: `${API_BASE_URL}/api/auth/register`,
  SEND_OTP: `${API_BASE_URL}/api/auth/send-otp`,
  VERIFY_OTP: `${API_BASE_URL}/api/auth/verify-otp`,
  VERIFY_OTP_REGISTER: `${API_BASE_URL}/api/auth/verify-otp-register`,
  RESET_PASSWORD: `${API_BASE_URL}/api/auth/reset-password`,
  LOGIN: `${API_BASE_URL}/api/auth/login`,
  LOGOUT: `${API_BASE_URL}/api/auth/logout`,
  VERIFY_TOKEN: `${API_BASE_URL}/api/auth/verify-token`,
  CHANGE_PASSWORD: `${API_BASE_URL}/api/auth/change-password`,

  // User endpoints
  USER_PROFILE: `${API_BASE_URL}/api/user/profile`,
  USER_BALANCE: `${API_BASE_URL}/api/user/balance`,
  USER_STATISTICS: `${API_BASE_URL}/api/user/statistics`,

  // Wallet endpoints
  WALLET_BALANCE: `${API_BASE_URL}/api/wallet/balance`,
  WALLET_DEPOSIT: `${API_BASE_URL}/api/wallet/deposit`,
  WALLET_WITHDRAW: `${API_BASE_URL}/api/wallet/withdraw`,
  WALLET_TRANSACTIONS: `${API_BASE_URL}/api/wallet/transactions`,
  WALLET_ACCOUNTS: `${API_BASE_URL}/api/wallet/accounts`,
  WALLET_ADD_ACCOUNT: `${API_BASE_URL}/api/wallet/add-account`,
  WALLET_PAYMENT_OPTIONS: `${API_BASE_URL}/api/wallet/payment-options`,
  WALLET_SUBMIT_DEPOSIT: `${API_BASE_URL}/api/wallet/submit-deposit`,
  WALLET_SUBMIT_WITHDRAWAL: `${API_BASE_URL}/api/wallet/submit-withdrawal`,
  WALLET_CHECK_PENDING: `${API_BASE_URL}/api/wallet/check-pending`,
  WALLET_CANCEL_WITHDRAW: `${API_BASE_URL}/api/wallet/cancel-withdraw`,
  ACCOUNT_STATEMENT: `${API_BASE_URL}/api/wallet/account-statement`,

  // Betting endpoints
  BETS_PLACE: `${API_BASE_URL}/api/bets/place`,
  BETS_USER: `${API_BASE_URL}/api/bets/user`,
  BETS_HISTORY: `${API_BASE_URL}/api/bets/history`,

  // Odds API endpoints (Betfair Exchange via odds-api.io)
  ODDS_SPORT: (sport) => `${API_BASE_URL}/api/odds?sport=${sport}`,
  ODDS_LIVE: (sport) => `${API_BASE_URL}/api/odds?sport=${sport}&live=1`,
  ODDS_EVENT: (id) => `${API_BASE_URL}/api/odds/event?id=${id}`,
  ODDS_SPORTS_LIST: `${API_BASE_URL}/api/odds/sports`,

  // Game endpoints
  GAMES_PROVIDERS: `${API_BASE_URL}/api/games/providers`,
  GAMES_LAUNCH: `${API_BASE_URL}/api/games/launch`,
  GAMES_CALLBACK: `${API_BASE_URL}/api/games/callback.php`,

  // Balance endpoints
  BALANCE_STREAM: `${API_BASE_URL}/api/balance/stream.php`,

  // Promotion endpoints
  PROMOTIONS_ACTIVE: `${API_BASE_URL}/api/promotions/active`,
  PROMOTIONS_REDEEM: `${API_BASE_URL}/api/promotions/redeem`,

  // Banner endpoints
  BANNERS: `${API_BASE_URL}/api/banners`,

  // Notification endpoints
  NOTIFICATIONS: `${API_BASE_URL}/api/notifications`,
  NOTIFICATIONS_PRIMARY: `${API_BASE_URL}/api/notifications/primary`,
  NOTIFICATIONS_ALL: `${API_BASE_URL}/api/notifications/all`,
};

export default API_BASE_URL;

// Global 401 handler — try refresh-once, then fall back to logout
const _origFetch = window.fetch;
let _refreshInFlight = null;

const _isAuthUrl = (input) => {
  try {
    const url = typeof input === 'string' ? input : (input?.url || '');
    return url.includes('/api/auth/');
  } catch { return false; }
};

const _tryRefreshToken = () => {
  if (_refreshInFlight) return _refreshInFlight;
  const token = localStorage.getItem('reddyannabook_token');
  if (!token) return Promise.resolve(null);
  _refreshInFlight = _origFetch('/api/auth/refresh-token', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
    .then(async (r) => {
      try {
        const d = await r.json();
        if (d?.success && d?.token) {
          localStorage.setItem('reddyannabook_token', d.token);
          return d.token;
        }
      } catch {}
      return null;
    })
    .catch(() => null)
    .finally(() => { _refreshInFlight = null; });
  return _refreshInFlight;
};

window.fetch = async function (...args) {
  const res = await _origFetch.apply(this, args);

  if (res.status !== 401) return res;

  // Don't loop on the auth endpoints themselves
  if (_isAuthUrl(args[0])) return res;

  // Peek at the body — single-session "another device" wins
  let data = null;
  try { data = await res.clone().json(); } catch {}
  if (data?.message && String(data.message).toLowerCase().includes('another device')) {
    localStorage.removeItem('reddyannabook_token');
    localStorage.removeItem('reddyannabook_user');
    if (!window._sessionExpiredShown) {
      window._sessionExpiredShown = true;
      alert('You have been logged in from another device. Please login again.');
      window.location.href = '/login';
    }
    return res;
  }

  // Try to refresh and retry the original request once
  const newToken = await _tryRefreshToken();
  if (newToken) {
    try {
      const [input, init = {}] = args;
      const nextInit = { ...init, headers: { ...(init.headers || {}) } };
      // Set Authorization header on retry (preserves method, body, etc.)
      if (nextInit.headers instanceof Headers) {
        nextInit.headers.set('Authorization', `Bearer ${newToken}`);
      } else {
        nextInit.headers['Authorization'] = `Bearer ${newToken}`;
      }
      return await _origFetch(input, nextInit);
    } catch {
      return res;
    }
  }

  // Refresh failed → logout silently (no raw "Invalid or expired token" popup)
  localStorage.removeItem('reddyannabook_token');
  localStorage.removeItem('reddyannabook_user');
  if (!window._sessionExpiredShown) {
    window._sessionExpiredShown = true;
    window.location.href = '/login?reason=expired';
  }
  return res;
};
